// UIFIX1 (docs/build-briefs/UIFIX1-reports-tables-controls.md; design/notes/console-design-fix-plan.md SF-3, SF-4, SF-5): the
// pure rules for what a report result table DRAWS. A result carries every field the server computed; a person reads a few. This
// file decides which columns are drawn, what a cell says in plain words, and which words ride under the first column. It never
// changes a figure and never touches the export: CSV, PDF and Sheets keep every column the server sent (DEC-477). No DOM here.
import { fromServer } from "./missingValue.ts";
import type { ResultTable, TableColumn, TableRow } from "./reportsExperienceModel.ts";

const lastPart = (key: string): string => key.split(".").pop() ?? key;

/** Fields a person never reads: ids, keys, references, hashes, indexes, internal kinds and the counts of internal records. */
const TECHNICAL: readonly RegExp[] = [
  /(^|\.)(key|id)$/i,
  /(Key|Id|Ids|Uuid|Reference|Hash|Index)$/,
  /(^|\.)(attributionKind|lineageKind|itemKindKey|categoryKey|kind)$/,
  /(^|\.)prior[A-Z]\w*$/,
  /SelectedDates/,
  /(unallocatedContribution|unvalued|mixed|belowZero|contributingLine|ordinaryPosting|remakePosting|undoPosting|receiptLine|pricedLine|unpricedLine|activeItem|includedItem)(Item|Line|Posting)?Count$/,
];
/** True for a field the on-screen table and totals never draw. The export still carries it. */
export function isTechnicalKey(key: string): boolean {
  return TECHNICAL.some((pattern) => pattern.test(key));
}

const STATUS_KEY = /(status|state|coverage|outcome|stage)$/i;
/** A status-like value that carries no news when every row says it. */
const DEFAULT_WORDS = new Set(["complete", "active", "built in", "none", "captured", "current only", "legacy", "no"]);

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_WEEK = /^(\d{4})-W(\d{2})$/;
const ISO_MONTH = /^(\d{4})-(\d{2})$/;
const ISO_STAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const NUMBER_TEXT = /^-?\d+(\.\d+)?$/;
const PREFIXED_ID = /^[a-z_]+:[0-9a-f-]{8,}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ENUM_WORDS: Readonly<Record<string, string>> = {
  partial_unpriced: "Partly priced",
  partial: "Partial",
  complete: "Complete",
  current_only: "Current only",
  draft: "Draft",
  placed: "Placed",
  received: "Received",
  partially_received: "Partly received",
  cancelled: "Cancelled",
};

export interface PlainOptions {
  /** The IANA zone a timestamp is read in (the location's, never UTC). */
  timeZone?: string;
  /** The year of today, so a date in this year carries none. */
  currentYear?: number;
}

/** "Tue Oct 20", with the year only when it is not this year. */
export function plainDate(iso: string, options: PlainOptions = {}): string {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const at = new Date(Date.UTC(year, month - 1, day, 12));
  const parts = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" }).formatToParts(at);
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? "";
  const base = `${part("weekday")} ${part("month")} ${part("day")}`;
  return options.currentYear === undefined || options.currentYear === year ? base : `${base}, ${year}`;
}
/** "Week of Oct 19" for an ISO week such as 2026-W43. */
export function plainWeek(text: string, options: PlainOptions = {}): string {
  const match = ISO_WEEK.exec(text);
  if (match === null) return text;
  const year = Number(match[1]);
  const week = Number(match[2]);
  // ISO week 1 holds January 4th; its Monday starts the count.
  const jan4 = new Date(Date.UTC(year, 0, 4, 12));
  const monday = new Date(jan4.getTime() - ((jan4.getUTCDay() + 6) % 7) * 86_400_000 + (week - 1) * 7 * 86_400_000);
  const parts = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).formatToParts(monday);
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value ?? "";
  const base = `Week of ${part("month")} ${part("day")}`;
  return options.currentYear === undefined || options.currentYear === monday.getUTCFullYear() ? base : `${base}, ${monday.getUTCFullYear()}`;
}
/** "Oct 20, 3:48 AM" read in the given zone. */
export function plainStamp(iso: string, options: PlainOptions = {}): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", ...(options.timeZone === undefined ? {} : { timeZone: options.timeZone }) });
}
/** "Sep 2026" for a month such as 2026-09. */
export function plainMonth(text: string): string {
  const match = ISO_MONTH.exec(text);
  if (match === null) return text;
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1, 12)).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}
/** Sentence case for an enum such as `partial_unpriced`. */
export function plainEnum(text: string): string {
  const known = ENUM_WORDS[text];
  if (known !== undefined) return known;
  if (!/^[a-z0-9]+(_[a-z0-9]+)+$/.test(text)) return text;
  const words = text.replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}
/** A word from the server turned into plain words: dates, weeks, stamps and enums read as people say them; an id reads as nothing. */
export function plainWord(text: string, options: PlainOptions = {}): string {
  if (text === "" || fromServer(text) === "-") return "";
  if (UUID.test(text) || PREFIXED_ID.test(text)) return "";
  if (ISO_STAMP.test(text)) return plainStamp(text, options);
  if (ISO_DATE.test(text)) return plainDate(text, options);
  if (ISO_WEEK.test(text)) return plainWeek(text, options);
  if (ISO_MONTH.test(text)) return plainMonth(text);
  return plainEnum(text);
}

export type ColumnShape = "lead" | "text" | "status" | "figure" | "change";
export interface DisplayColumn {
  key: string;
  label: string;
  shape: ColumnShape;
  numeric: boolean;
  change: boolean;
}
export interface DisplayTable {
  columns: DisplayColumn[];
  lead: DisplayColumn | undefined;
  /** The one muted line under the lead (the franchisee), when a row has one. */
  noteKey: string | undefined;
  /** What a cell says. Empty for a missing value: never the letters N/A. */
  cell(row: TableRow, column: DisplayColumn): string;
  note(row: TableRow): string;
  /** The total row's cell, or "" when the server sent none for the column. */
  totalCell(column: DisplayColumn): string;
}

const present = (value: unknown): boolean => value !== null && value !== undefined && value !== "" && !(typeof value === "number" && !Number.isFinite(value));

/** The columns that name a place; a one-location report never draws them. */
const PLACE_KEYS = /(^|\.)(locationName|franchisee)$/;

/** Group words that tell two columns of the same name apart ("Recorded delivered" and "Complete delivered"). */
function groupWord(key: string): string {
  const parts = key.replace(/^current\./, "").split(".");
  const parent = parts.length > 1 ? parts[parts.length - 2] ?? "" : "";
  return /^(recorded|complete)$/i.test(parent) ? parent.charAt(0).toUpperCase() + parent.slice(1).toLowerCase() : "";
}

export function displayTable(table: ResultTable, options: PlainOptions & { singleLocation: boolean }): DisplayTable {
  const values = (key: string): unknown[] => table.rows.map((row) => row.values[key]);
  const labelKeys = new Set(table.columns.map((column) => lastPart(column.key)));
  let candidates = table.columns.filter((column) => !isTechnicalKey(column.key));
  // A place is named once, in the scope: a report for one location does not repeat it on every row.
  if (options.singleLocation) candidates = candidates.filter((column) => !PLACE_KEYS.test(column.key));
  // `category` is the raw word when a readable label is also sent.
  if (labelKeys.has("categoryLabel")) candidates = candidates.filter((column) => lastPart(column.key) !== "category");
  // A column empty for every row is not drawn.
  candidates = candidates.filter((column) => table.rows.length === 0 || values(column.key).some(present));
  const isNumberText = (column: TableColumn): boolean => {
    const cells = values(column.key).filter(present);
    return !column.numeric && cells.length > 0 && cells.every((value) => typeof value === "string" && NUMBER_TEXT.test(value));
  };
  const shapeOf = (column: TableColumn): ColumnShape => {
    if (column.change) return "change";
    if (column.numeric || isNumberText(column)) return "figure";
    if (STATUS_KEY.test(lastPart(column.key))) return "status";
    return "text";
  };
  let shaped = candidates.map((column) => ({ column, shape: shapeOf(column) }));
  // A one-location report keeps the location as its row label only when nothing else can name the row (a summary of one place).
  const placeColumn = table.columns.find((column) => /(^|\.)locationName$/.test(column.key));
  if (options.singleLocation && placeColumn !== undefined && !shaped.some((entry) => entry.shape === "text") && table.rows.length > 0 && values(placeColumn.key).some(present))
    shaped.unshift({ column: placeColumn, shape: "text" });
  // A text column that repeats an earlier text column on every row says nothing new (category and subcategory both "rum").
  const seen: string[] = [];
  shaped = shaped.filter(({ column, shape }) => {
    if (shape !== "text") return true;
    const signature = JSON.stringify(values(column.key));
    if (seen.includes(signature)) return false;
    seen.push(signature);
    return true;
  });
  // A status or flag that every row repeats, with a default word, is the default: it is not drawn.
  shaped = shaped.filter(({ column, shape }) => {
    if (shape !== "status" && typeof values(column.key).find(present) !== "boolean") return true;
    const cells = values(column.key).filter(present).map((value) => (typeof value === "boolean" ? (value ? "yes" : "no") : plainEnum(String(value)).toLowerCase()));
    if (cells.length === 0) return false;
    const same = cells.every((cell) => cell === cells[0]);
    return !(same && (cells.length > 1 || DEFAULT_WORDS.has(cells[0] as string)));
  });
  // The first text column leads; the franchisee rides as the one muted line under a location, and is a plain column otherwise.
  const text = shaped.filter((entry) => entry.shape === "text");
  const place = text.find((entry) => /(^|\.)locationName$/.test(entry.column.key));
  const franchisee = text.find((entry) => /(^|\.)franchisee$/.test(entry.column.key));
  const leadEntry = text.find((entry) => !/(^|\.)franchisee$/.test(entry.column.key));
  const noteEntry = franchisee !== undefined && leadEntry !== undefined && leadEntry === place ? franchisee : undefined;
  const ordered = [
    ...(leadEntry === undefined ? [] : [leadEntry]),
    ...text.filter((entry) => entry !== leadEntry && entry !== noteEntry),
    ...shaped.filter((entry) => entry.shape === "status"),
    ...shaped.filter((entry) => entry.shape === "figure"),
    ...shaped.filter((entry) => entry.shape === "change"),
  ];
  const baseLabel = (column: TableColumn) => column.label;
  const counts = new Map<string, number>();
  for (const entry of ordered) counts.set(baseLabel(entry.column), (counts.get(baseLabel(entry.column)) ?? 0) + 1);
  const columns: DisplayColumn[] = ordered.map(({ column, shape }) => {
    const group = (counts.get(baseLabel(column)) ?? 0) > 1 ? groupWord(column.key) : "";
    return {
      key: column.key,
      label: group === "" ? column.label : `${group} ${column.label.charAt(0).toLowerCase()}${column.label.slice(1)}`,
      shape: leadEntry !== undefined && column === leadEntry.column ? "lead" : shape,
      numeric: shape === "figure" || shape === "change",
      change: shape === "change",
    };
  });
  const lead = columns.find((column) => column.shape === "lead");
  const plain = (value: unknown, column: DisplayColumn, fallback: string): string => {
    if (!present(value)) return "";
    if (typeof value === "string") return column.shape === "figure" ? value : plainWord(value, options);
    return fallback;
  };
  return {
    columns,
    lead,
    noteKey: noteEntry?.column.key,
    cell: (row, column) => plain(row.values[column.key], column, row.text[column.key] ?? ""),
    // The franchisee line is dropped when it only repeats the location's own name (no repeated sub-lines).
    note: (row) => {
      if (noteEntry === undefined || lead === undefined) return "";
      const text = plain(row.values[noteEntry.column.key], { shape: "text" } as DisplayColumn, "");
      return text === plain(row.values[lead.key], lead, "") ? "" : text;
    },
    totalCell: (column) => {
      const total = table.total;
      if (total === null) return "";
      if (column === (lead ?? columns[0])) return total.label;
      if (!present(total.values[column.key])) return "";
      return total.text[column.key] ?? "";
    },
  };
}

/** The value detail for one row, in the order the table draws them, for the row sheet: every readable field in full. */
export function rowDetails(view: DisplayTable, row: TableRow): Array<{ label: string; text: string }> {
  const lines: Array<{ label: string; text: string }> = [];
  for (const column of view.columns) {
    const text = view.cell(row, column);
    if (text !== "") lines.push({ label: column.label, text });
  }
  const note = view.note(row);
  if (note !== "") lines.push({ label: "Franchisee", text: note });
  return lines;
}

/** Plain words for a chart label: a leading "Location name: " that the scope already says is dropped. */
export function shortChartLabel(label: string, locationNames: readonly string[]): string {
  for (const name of locationNames) {
    if (name !== "" && label.startsWith(`${name}: `)) return label.slice(name.length + 2);
  }
  return label;
}
